import { styled } from '@mui/material';
import {
    DataGridPro,
    DataGridProProps,
    GridValidRowModel,
    GridPinnedColumnFields,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridRowHeightParams,
} from '@mui/x-data-grid-pro';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import {
    headingFontBold,
    defaultBorder,
    bodyFont,
    vars,
    dataGridRowsHeight,
} from '../../../../Theme';
import { getGridTranslations } from '../DocTableUtils';
import DocTableColumnMenu from '../DocTableColumnMenu';
import DocTableFilterPanel from '../DocTableFilterPanel';
import { intersection } from 'lodash';

/** CSS class that is applied to a row that is open in the application sidepanel. */
const cssActive = 'AppSidepanelRowFocus';

/**
 * Stylised DataGrid Component.
 */
const StyledDataGridPro = styled(DataGridPro)(() => {
    return {
        ...bodyFont,
        WebkitFontSmoothing: 'auto',
        letterSpacing: 'normal',
        overflow: 'hidden',
        color: vars.font,
        borderRadius: 0,
        fontSize: 14,
        border: 0,

        [`& .${cssActive}`]: {
            position: 'relative',

            '&:after': {
                border: `2px solid ${vars.brandGreen}`,
                position: 'absolute',
                content: '""',
                zIndex: -1,
                bottom: 0,
                right: 0,
                left: 0,
                top: 0,
            },
        },

        '& .MuiDataGrid-pinnedColumns': {
            '& .MuiDataGrid-cell:only-child': {
                borderRight: 0,
            },

            [`& .${cssActive}:after`]: {
                borderRightWidth: 0,
            },
        },

        '& .MuiDataGrid-pinnedColumnHeaders': {
            '& .MuiDataGrid-columnHeader:only-child': {
                borderRight: 0,
            },
        },

        '& .MuiDataGrid-virtualScrollerRenderZone': {
            [`& .MuiDataGrid-row.${cssActive}:after`]: {
                borderLeftWidth: 0,
            },
        },

        '& .MuiDataGrid-main': {
            borderTop: defaultBorder,
        },

        '& .MuiDataGrid-main, & .MuiDataGrid-footerContainer': {
            borderLeft: defaultBorder,
        },

        '& .MuiDataGrid-columnHeaders': {
            borderRadius: 0,
        },

        '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
            borderRight: defaultBorder,
            display: 'flex',
            alignItems: 'center',
        },

        '& .MuiDataGrid-columnHeaderTitle': {
            color: vars.black,
            ...headingFontBold,
        },

        '& .MuiDataGrid-iconSeparator': {
            opacity: 0,
        },

        '& .MuiDataGrid-footerContainer': {
            minHeight: 44,
        },
        '& .MuiDataGrid-headerFilterRow ': {
            '.MuiDataGrid-columnHeader': {
                minHeight: '40px',
                maxHeight: '40px',
                width: '100%',
            },
            '.MuiFormLabel-root': {
                fontSize: '14px',
            },
            '.MuiInputBase-root': {
                marginTop: '10px',
            },
        },
        '& .MuiDataGrid-cellCheckbox': {
            minWidth: '50px',
        },
    };
});

/**
 * Stylised and preconfigured DataGrid component for showing documents in a project database.
 */
export default (props: IStyledDataGridProps) => {
    const { initialState, slotProps, slots, fieldsWithMultilineType, ...otherProps } = props;

    /** Get relevant values from Redux store. */
    const { gridDensity, headerFilters } = useSelector(
        (store: IAppReduxStore) => store.userSettings.data,
    );
    const targetNoteId = useSelector(
        (store: IAppReduxStore) => store.projectSidepanel.targetNoteId,
    );
    const importEditingActive = useSelector(
        (store: IAppReduxStore) => store.importData.editingActive,
    );

    /** State slice that controls the position of the Filter menu shown in the DataGrid's toolbar. */
    const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);

    const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({
        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    });

    const handlePinnedColumnsChange = useCallback(
        (updatedPinnedColumns: GridPinnedColumnFields) => {
            setPinnedColumns(updatedPinnedColumns);
        },
        [],
    );

    const getRowHeight = useCallback(
        ({ densityFactor, model }: GridRowHeightParams) => {
            const rowsHeight = dataGridRowsHeight * densityFactor;

            if (!fieldsWithMultilineType?.length) return rowsHeight;

            const fieldsIntersection = intersection(Object.keys(model), fieldsWithMultilineType);
            return fieldsIntersection.length !== 0 && fieldsIntersection.some(f => !!model[f])
                ? 'auto'
                : rowsHeight;
        },
        [fieldsWithMultilineType],
    );

    return (
        <StyledDataGridPro
            onCellKeyDown={(_params, ev) => {
                if (importEditingActive && [' ', 'ArrowLeft', 'ArrowRight'].includes(ev.key)) {
                    ev.stopPropagation();
                }
            }}
            onCellEditStart={(_params, ev) => {
                ev.defaultMuiPrevented = true;
            }}
            /** Override components with styled alternatives. */
            slots={{
                columnMenu: DocTableColumnMenu,
                filterPanel: DocTableFilterPanel,
                headerFilterMenu: null,
                ...slots,
            }}
            /** Additional props for sub-components. */
            slotProps={{
                ...slotProps,
                panel: {
                    anchorEl: filterButtonEl,
                    placement: 'bottom-end',
                    ...slotProps.panel,
                },
                toolbar: {
                    setFilterButtonEl,
                    ...slotProps.toolbar,
                },
                headerFilterCell: {
                    InputComponentProps: {
                        size: 'small',
                    },
                },
            }}
            /** Set grid initial state. */
            initialState={{
                /** Set row density. */
                ...initialState,
            }}
            density={gridDensity}
            /** Update DataGrid translations. */
            localeText={getGridTranslations()}
            /** Use data object's 'NoteId' field as a unique identifier. */
            getRowId={row => row.NoteId}
            /** Decrease scrollbar size to fit with global application styles. */
            scrollbarSize={6}
            /** Set header and row height. */
            columnHeaderHeight={40}
            getRowHeight={getRowHeight}
            /** Automatically calculate the height of the row's Master Detail section. */
            getDetailPanelHeight={() => 'auto'}
            /** Make category folders unselectable. */
            isRowSelectable={useCallback(({ row }) => !row.CategoryName, [])}
            /** Adds a class to the row if it is open in the sidepanel. */
            getRowClassName={useCallback(
                ({ row }) => (row.NoteId === targetNoteId ? cssActive : ''),
                [targetNoteId],
            )}
            /** Allow row selection. */
            checkboxSelection
            /** Prevent the default DataGrid selection behaviour. */
            disableRowSelectionOnClick
            /** Do not show the grid's footer. */
            hideFooter
            /** Set initial header filters. */
            headerFilters={headerFilters}
            /** Set initial pinned columns. */
            pinnedColumns={pinnedColumns}
            /** Update pinned columns when they change. */
            onPinnedColumnsChange={handlePinnedColumnsChange}
            {...otherProps}
        />
    );
};

/**
 * Interface that describes the props for a StyledDataGrid component.
 */
export interface IStyledDataGridProps extends DataGridProProps<GridValidRowModel> {
    fieldsWithMultilineType?: string[];
    /** (OPTIONAL) Component to show in the event of an error being thrown. */
    errorOverlay?: React.JSXElementConstructor<unknown>;
}
